import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { fmtPrice } from "@/lib/format";
import { stashTickerProvenance } from "@/lib/ticker_provenance";
import { emitLifecycle, newClientTraceId } from "@/lib/lifecycle";
import { startAnalyze } from "@/lib/startAnalyze";
import { useCompanyIdentities } from "@/lib/useCompanyIdentities";
import CompanyLogo from "@/components/common/CompanyLogo";

/**
 * StockSearch
 *
 * Mobile-first behavior:
 *  - The suggestion dropdown is CLOSED by default on mobile so the hero,
 *    CTA, and featured opportunity card stay visible.
 *  - The dropdown opens only on user-initiated interaction (tap / typing).
 *  - On mobile the dropdown caps at ~4 rows, then scrolls internally.
 *  - Premium mobile dropdown: softer shadow + stronger glass blur + smooth fade-in.
 *
 * Desktop behavior is preserved (autoFocus + popular tickers shown immediately).
 */
export default function StockSearch({
  className = "",
  testIdPrefix = "stock-search",
  placeholder = "Search stocks (e.g. AAPL, NVDA)",
  autoFocus = false,
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Phase 8.LP.LR — batched, cache-only company identity lookup for
  // the current result set. Fires a single GET /api/companies/
  // identity request per unique ticker set. On error the hook
  // resolves to fallbacks so the dropdown never blocks on network.
  const resultTickers = useMemo(
    () => (Array.isArray(results) ? results.map((r) => r.ticker) : []),
    [results]
  );
  const { getIdentity } = useCompanyIdentities(resultTickers);

  // Mobile detection (≤ 639px aligns with Tailwind's `sm:` breakpoint).
  // Memoised on mount; we also listen to width changes so an in-page
  // resize / orientation change keeps the behaviour correct.
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 639px)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 639px)");
    const onChange = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // Apply autoFocus only on desktop. On mobile we deliberately skip it so
  // the page does not auto-open a half-screen dropdown over the hero.
  const shouldAutoFocus = autoFocus && !isMobile;

  // Close dropdown on outside click / tap
  useEffect(() => {
    function onPointerDown(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!q.trim()) {
        try {
          setLoading(true);
          const res = await api.get("/popular", { params: { limit: 3 } });
          if (!cancelled) {
            setResults(res.data.results || []);
            setHighlight(0);
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }
      try {
        setLoading(true);
        const res = await api.get("/search", { params: { q, limit: 8 } });
        if (!cancelled) {
          setResults(res.data.results || []);
          setHighlight(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const t = setTimeout(run, 120);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q]);

  // Phase 6.AJ — Ticker provenance tracking. Stash the EXACT typed query
  // (and the resolved navigation target) in sessionStorage at submit time
  // so the next Analyze.js mount can include `typed_ticker` in its
  // lifecycle telemetry. Without this we cannot prove "the user typed MU
  // and the URL became /analyze/MU" — we only see useParams later.
  //
  // Phase 6.AK — Also emit a `search_submit` lifecycle event so the
  // Owner Console can see, in real time, what the user typed and what
  // ticker we resolved that to (e.g. typed "Micron" → target "MU" via
  // a /search suggestion click). This is the canonical evidence for
  // "I typed Micron on the homepage and submitted" appearing in the
  // ticker provenance trace.
  // Phase 6.AN — ALL entry paths go through startAnalyze() — the single
  // unified function that normalizes the ticker, stashes provenance,
  // emits unified lifecycle telemetry, and navigates. The Analyze route
  // is wrapped in AnalyzeRouteWrapper which keys by ticker, guaranteeing
  // a fresh mount on every navigation (no stale React state).
  //
  // The legacy `stashProvenance()` helper below is kept ONLY to also
  // emit the per-component-instance `search_submit` / `home_search_submitted`
  // events so the Owner Console can still distinguish "homepage submit"
  // from "navbar submit" from "error-page submit". startAnalyze itself
  // emits the canonical `analyze_started_by_user` event so the unified
  // chain works even when one of the legacy emits fails silently.
  const stashProvenance = (target, source, item = null) => {
    // Phase 6.BE — When the source is a suggestion click, the item
    // object carries the canonical mapping (typed company name → ticker
    // symbol). Pass it through so Owner Console can distinguish a
    // genuine "MU vs HD" route mismatch from a benign "user typed
    // Micron, suggestion mapped to MU".
    stashTickerProvenance({
      typed: q,
      target,
      source,
      selected_label: item?.name || item?.label || "",
      selected_symbol: item?.ticker || item?.symbol || "",
    });
    try {
      const pathname = (typeof window !== "undefined") ? window.location.pathname : null;
      const baseExtra = {
        typed_raw: q || "",
        typed_uppercase: (q || "").trim().toUpperCase(),
        target: (target || "").toUpperCase(),
        source,
        pathname,
        looks_like_company_name: !!(q && q.length > 5 && !/^[A-Z]{1,5}$/i.test(q.trim())),
        normalized_from_company_name:
          !!(q && (q || "").trim().toUpperCase() !== (target || "").toUpperCase()),
      };
      const traceId = newClientTraceId();
      emitLifecycle("search_submit", {
        ticker: (target || "").toUpperCase() || null,
        client_trace_id: traceId,
        extra: baseExtra,
      });
      if (pathname === "/" || pathname === "" || pathname === null) {
        emitLifecycle("home_search_submitted", {
          ticker: (target || "").toUpperCase() || null,
          client_trace_id: traceId,
          extra: {
            ...baseExtra,
            destination_path: `/analyze/${(target || "").toUpperCase()}`,
          },
        });
      }
    } catch { /* swallow */ }
  };

  const handleSelect = (item) => {
    if (!item) return;
    setOpen(false);
    stashProvenance(item.ticker, "stock_search:suggestion", item);
    startAnalyze({
      navigate, ticker: item.ticker, typedRaw: q,
      source: "stock_search:suggestion",
    });
  };

  const onKeyDown = (e) => {
    if (!open) setOpen(true);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min((results?.length || 1) - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = results[highlight];
      if (item) {
        handleSelect(item);
      } else if (q.trim()) {
        stashProvenance(q, "stock_search:enter_typed");
        startAnalyze({
          navigate, ticker: q, typedRaw: q,
          source: "stock_search:enter_typed",
        });
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      inputRef.current && inputRef.current.blur && inputRef.current.blur();
    }
  };

  // Focus handler:
  //   - Desktop: open immediately (preserves current UX).
  //   - Mobile : open immediately too \u2014 but because we suppressed autoFocus,
  //              this will only fire when the user actually taps the input,
  //              which is the desired behaviour.
  const onFocus = () => setOpen(true);

  // Pointer-down on the input wrapper acts as the "user tapped" trigger
  // on mobile, ensuring the dropdown opens reliably on first tap even
  // on browsers that delay the focus event.
  const onWrapperPointerDown = () => setOpen(true);

  // Typing also opens the dropdown (mobile users who long-press \u2192 paste
  // or use voice input may bypass focus).
  const onChange = (e) => {
    setQ(e.target.value);
    setOpen(true);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`} data-testid={`${testIdPrefix}-root`}>
      <div
        onPointerDown={onWrapperPointerDown}
        className="flex items-center h-11 sm:h-12 rounded-xl border border-border/70 bg-card/70 backdrop-blur px-3 focus-within:ring-2 focus-within:ring-[color:var(--app-glow-blue)] transition-shadow"
      >
        <Search className="w-4 h-4 text-muted-foreground" />
        <input
          ref={inputRef}
          data-testid={`${testIdPrefix}-input`}
          autoFocus={shouldAutoFocus}
          value={q}
          onChange={onChange}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="flex-1 bg-transparent outline-none px-3 text-sm sm:text-base placeholder:text-muted-foreground"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          inputMode="search"
        />
        <kbd className="hidden sm:inline-flex items-center gap-1 rounded border border-border/70 bg-muted/40 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">↑↓ Enter</kbd>
      </div>
      {open && (results.length > 0 || loading) && (
        <div
          data-testid={`${testIdPrefix}-results`}
          className="absolute z-[80] left-0 right-0 mt-2 rounded-xl border border-border/55 bg-popover/85 sm:bg-popover/95 backdrop-blur-2xl sm:backdrop-blur-xl overflow-hidden origin-top animate-search-pop"
          style={{
            boxShadow:
              "0 18px 44px -22px rgba(0,0,0,0.55), 0 8px 16px -10px rgba(0,0,0,0.40), 0 0 0 1px rgba(255,255,255,0.025) inset",
          }}
        >
          {!q.trim() && (
            <div className="px-3.5 py-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground/70 border-b border-border/40 bg-card/30">
              Popular tickers
            </div>
          )}
          <ul
            role="listbox"
            className="overflow-y-auto py-1 max-h-[140px] sm:max-h-[152px] overscroll-contain"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {loading && results.length === 0 && (
              <li className="px-3.5 py-3 text-sm text-muted-foreground">Loading...</li>
            )}
            {results.map((r, i) => {
              const ident = getIdentity(r.ticker);
              return (
              <li
                key={r.ticker}
                data-testid={`${testIdPrefix}-result-item`}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => handleSelect(r)}
                className={`group px-3.5 py-2.5 cursor-pointer flex items-center justify-between gap-3 transition-colors duration-150 ${
                  i === highlight ? "bg-secondary/55" : "hover:bg-secondary/35 active:bg-secondary/55"
                }`}
                role="option"
                aria-selected={i === highlight}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  {/* Phase 8.LP.LR — company logo (cache-only). Falls
                      back to ticker text when no logo is cached, so
                      the row still reads correctly without a network
                      round-trip. */}
                  <CompanyLogo
                    ticker={r.ticker}
                    name={ident.name || r.name}
                    logoUrl={ident.logo_url}
                    size="xs"
                    fallbackText={r.ticker.slice(0, 2)}
                    testId={`${testIdPrefix}-result-logo-${r.ticker}`}
                  />
                  <span className="font-mono text-[11.5px] sm:text-sm w-14 shrink-0 text-foreground/95 tabular tracking-wider">
                    {r.ticker}
                  </span>
                  <span className="text-[13px] sm:text-sm truncate text-muted-foreground">{r.name}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {r.sector ? (
                    <span className="text-[11px] text-muted-foreground/70 hidden sm:inline">{r.sector}</span>
                  ) : null}
                  {r.price != null ? (
                    <span className="font-mono tabular text-[13px] sm:text-sm text-foreground/90">${fmtPrice(r.price)}</span>
                  ) : (
                    <span className="text-[11px] uppercase tracking-wider text-muted-foreground/70 hidden sm:inline">{r.exchange || ""}</span>
                  )}
                </div>
              </li>
              );
            })}
            {!loading && q.trim() && results.length === 0 && (
              <li className="px-3 py-3 text-sm text-muted-foreground">
                No matches – press Enter to try “{q.trim().toUpperCase()}” anyway.
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
